import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';

const styles = `
  .sec-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 28px;
  }
  .sec-card {
    background: #fff;
    border-radius: 12px;
    padding: 20px 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  }
  .sec-card-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
  }
  .sec-card-value {
    font-size: 1.8rem;
    font-weight: 700;
    color: #2C2C2C;
    line-height: 1;
  }
  .sec-card-value.warn { color: #C0392B; }
  .sec-filter {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }
  .sec-filter button {
    padding: 6px 14px;
    border: 1px solid #E0DDD8;
    border-radius: 20px;
    background: #fff;
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: 500;
    color: #555;
    cursor: pointer;
    transition: all 0.15s;
  }
  .sec-filter button:hover { border-color: #E8845A; color: #E8845A; }
  .sec-filter button.active {
    background: #FFF5F0;
    border-color: #E8845A;
    color: #E8845A;
    font-weight: 600;
  }
  .sec-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: hidden;
  }
  .sec-table th {
    text-align: left;
    padding: 12px 16px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #EDE9E4;
    background: #FAFAF8;
  }
  .sec-table td {
    padding: 12px 16px;
    font-size: 0.88rem;
    color: #2C2C2C;
    border-bottom: 1px solid #F0EDE8;
  }
  .sec-table tr:last-child td { border-bottom: none; }
  .sec-table tr:hover td { background: #FDFCFB; }
  .type-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .type-login_success { background: #F0FFF4; color: #27AE60; }
  .type-login_failure { background: #FFF0EE; color: #C0392B; }
  .type-session_start { background: #EBF5FB; color: #2980B9; }
  .type-default { background: #F5F5F5; color: #666; }
  .detail-json {
    font-size: 0.78rem;
    color: #666;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .loading-state { color: #888; font-size: 0.9rem; padding: 20px 0; }
  .error-state { color: #C0392B; font-size: 0.9rem; padding: 20px 0; }
  .empty-state { color: #888; font-size: 0.9rem; padding: 32px 0; text-align: center; }
`;

const EVENT_TYPES = [
  { value: '', label: 'All' },
  { value: 'login_success', label: 'Login Success' },
  { value: 'login_failure', label: 'Login Failure' },
  { value: 'session_start', label: 'Session Start' },
];

function fmtDate(iso) {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function fmtDetail(detail) {
  if (!detail || Object.keys(detail).length === 0) return '\u2014';
  return Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join(', ');
}

function typeBadgeClass(type) {
  if (['login_success', 'login_failure', 'session_start'].includes(type)) return `type-${type}`;
  return 'type-default';
}

export default function AdminSecurity() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  function load(type) {
    setLoading(true);
    setError('');
    adminApi.securityEvents(type || undefined, 50)
      .then(data => {
        setEvents(data.events || []);
        setSummary(data.summary || null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(filter); }, [filter]);

  return (
    <AdminLayout title="Security Events">
      <style>{styles}</style>

      {summary && (
        <div className="sec-summary">
          <div className="sec-card">
            <div className="sec-card-label">Logins Today</div>
            <div className="sec-card-value">{summary.logins_today}</div>
          </div>
          <div className="sec-card">
            <div className="sec-card-label">Failed Logins Today</div>
            <div className={`sec-card-value${summary.failed_logins_today > 0 ? ' warn' : ''}`}>
              {summary.failed_logins_today}
            </div>
          </div>
          <div className="sec-card">
            <div className="sec-card-label">Sessions Today</div>
            <div className="sec-card-value">{summary.sessions_today}</div>
          </div>
        </div>
      )}

      <div className="sec-filter">
        {EVENT_TYPES.map(t => (
          <button
            key={t.value}
            className={filter === t.value ? 'active' : ''}
            onClick={() => setFilter(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <p className="loading-state">Loading...</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {!loading && events.length === 0 && (
        <p className="empty-state">No security events recorded yet.</p>
      )}

      {events.length > 0 && (
        <table className="sec-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>User</th>
              <th>Detail</th>
              <th>IP</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {events.map(e => (
              <tr key={e.id}>
                <td>
                  <span className={`type-badge ${typeBadgeClass(e.event_type)}`}>
                    {e.event_type}
                  </span>
                </td>
                <td>{e.user_email || '\u2014'}</td>
                <td><span className="detail-json">{fmtDetail(e.detail)}</span></td>
                <td>{e.ip_address || '\u2014'}</td>
                <td>{fmtDate(e.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminLayout>
  );
}
